import { ChevronLeft, ChevronRight, type LucideIcon, X, ZoomIn, ZoomOut } from 'lucide-react';
import { type CSSProperties, type RefObject, useEffect, useRef, useState } from 'react';
import { INK } from './elt-model';
import { type EngineMode, EltEngine, type ScopeFocus } from './engine';
import { LaserWriter } from './laser-writer';
import { PLANET_SKY } from './sky';

// The Santiago hero: the Extremely Large Telescope on Cerro Armazones builds itself facing
// you, its lasers write the page's title in the air in front of it, then the real sky over
// Santiago lights up and the telescope follows the pointer; a click looks through it.
//
// Everything 3D lives in EltEngine; this component owns the few overlays (the hovered
// constellation's name, the telescope's field stop, planet markers and read-out) and
// wires input to the engine.

type Props = {
  onEyepieceChange?: (open: boolean) => void;
  // false for still captures (the OG card): no labels, nothing written, no hints
  chrome?: boolean;
  // the page's title: the lasers trace its logo in front of the telescope
  title?: RefObject<HTMLElement | null>;
  // the title is written: the page shows its real logo, then the place and dates, then the
  // call to action — all three rendered hidden from the server on, so nothing shows early.
  // Called at once when there is no writing (reduced motion, no WebGL).
  onTitle?: () => void;
};

const TAP_SLOP = 10; // px a press may travel and still count as a tap
const NUDGE_DEG = 2; // per arrow key, through the telescope
const ZOOM_STEP = 2; // the field widens or narrows by this per +/− press

// Outside the field stop, blur in layers, each starting further out and feathered in, so
// the sky goes from just off-focus at the rim to a deep blur at the screen's edges.
const FIELD_BLUR = [
  { blur: 1.5, from: 0, feather: 40 },
  { blur: 4, from: 50, feather: 120 },
  { blur: 8, from: 180, feather: 220 },
];

// the planets that are up that night: the others never get a marker
const PLANETS_UP = PLANET_SKY.filter((p) => p.dir.y > 0);

// A control through the telescope: only its icon, until hovered (or focused), when a small
// caption — and the key that does the same — appears above it.
function ScopeButton({ label, keyHint, icon: Icon, onClick }: { label: string; keyHint?: string; icon: LucideIcon; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="group relative flex size-10 items-center justify-center text-primary/55 outline-none transition-colors hover:text-primary focus-visible:text-primary"
    >
      <Icon className="size-[18px]" strokeWidth={1.5} aria-hidden="true" />
      <span className="-translate-x-1/2 pointer-events-none absolute bottom-full left-1/2 mb-1 translate-y-1 whitespace-nowrap font-mono text-[9px] text-primary/80 uppercase tracking-[0.22em] opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100">
        {label}
        {keyHint && <span className="hidden text-primary/45 md:inline"> · {keyHint}</span>}
      </span>
    </button>
  );
}

export function TelescopeHero({ onEyepieceChange, chrome = true, title, onTitle }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const traceRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<EltEngine | null>(null);
  const nameRef = useRef<HTMLParagraphElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const planetRefs = useRef(new Map<string, HTMLElement>());
  const [mode, setMode] = useState<EngineMode>('intro');
  const [focus, setFocus] = useState<ScopeFocus | null>(null);
  const [failed, setFailed] = useState(false);
  const [built, setBuilt] = useState(false); // the telescope is whole: its caption may show
  const onEyepieceChangeRef = useRef(onEyepieceChange);
  onEyepieceChangeRef.current = onEyepieceChange;
  const onTitleRef = useRef(onTitle);
  onTitleRef.current = onTitle;

  useEffect(() => {
    const host = canvasHostRef.current;
    const root = rootRef.current;
    if (!host || !root) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The lasers trace the logo where the page has laid it out (hidden, until onTitle).
    const logo = chrome ? (title?.current?.querySelector<HTMLElement>('.font-logo') ?? null) : null;
    let writer: LaserWriter | null = null;
    if (logo && traceRef.current && !reduceMotion) {
      writer = new LaserWriter(traceRef.current, logo, INK);
      void writer.prepare();
    }
    const showLogo = () => onTitleRef.current?.();

    let engine: EltEngine;
    try {
      engine = new EltEngine(
        host,
        {
          name: nameRef.current,
          field: fieldRef.current,
          planets: planetRefs.current,
          writer,
        },
        {
          onTitle: showLogo,
          onBuilt: () => setBuilt(true),
          onFocus: setFocus,
          onMode: (next) => {
            setMode(next);
            if (next !== 'eyepiece') setFocus(null);
            onEyepieceChangeRef.current?.(next === 'diving' || next === 'eyepiece');
          },
        },
        { skipIntro: reduceMotion },
      );
    } catch {
      // no WebGL: the hero keeps its title and button over a plain night
      showLogo();
      setBuilt(true);
      setFailed(true);
      return;
    }
    engineRef.current = engine;
    engine.start();

    const resizeObserver = new ResizeObserver(() => {
      engine.resize();
      // the title reflows with the page: retrace it where it now stands
      void writer?.prepare();
    });
    resizeObserver.observe(host);
    const visibility = new IntersectionObserver((entries) => {
      engine.setVisible(entries.some((entry) => entry.isIntersecting) && !document.hidden);
    });
    visibility.observe(root);
    const onVisibility = () => engine.setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);

    const toNdc = (event: PointerEvent) => {
      const rect = root.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      return {
        x: (x / rect.width) * 2 - 1,
        y: -(y / rect.height) * 2 + 1,
        inside: x >= 0 && y >= 0 && x <= rect.width && y <= rect.height,
      };
    };
    // A press that stays put is a tap; one that travels drags the telescope's view; two
    // fingers pinch its field.
    let down: { x: number; y: number; lastX: number; lastY: number; dragging: boolean } | null = null;
    const touches = new Map<number, { x: number; y: number }>();
    const spread = () => {
      const [a, b] = [...touches.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };
    const onMove = (event: PointerEvent) => {
      if (touches.has(event.pointerId) && touches.size === 2) {
        const before = spread();
        touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (before > 0) engine.zoom(before / Math.max(1, spread()));
        return;
      }
      if (down) {
        if (!down.dragging && Math.hypot(event.clientX - down.x, event.clientY - down.y) > TAP_SLOP) down.dragging = true;
        if (down.dragging) engine.pan(event.clientX - down.lastX, event.clientY - down.lastY);
        down.lastX = event.clientX;
        down.lastY = event.clientY;
      }
      if (event.pointerType === 'touch') return;
      const p = toNdc(event);
      engine.setPointer(p.x, p.y, p.inside);
    };
    const onLeave = () => engine.setPointer(0, 0, false);
    const onDown = (event: PointerEvent) => {
      if (event.pointerType === 'touch') touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
      // a second finger turns the gesture into a pinch: no tap, no drag
      if (touches.size > 1 && down) down.dragging = true;
      else down = { x: event.clientX, y: event.clientY, lastX: event.clientX, lastY: event.clientY, dragging: false };
    };
    // the browser took the gesture over (a scroll that began on the hero): forget it
    const onCancel = (event: PointerEvent) => {
      touches.delete(event.pointerId);
      if (touches.size === 0) down = null;
    };
    const onUp = (event: PointerEvent) => {
      touches.delete(event.pointerId);
      if (!down) return;
      if (touches.size > 0) return; // a pinch ends when the last finger lifts
      const tap = !down.dragging;
      down = null;
      if (!tap || !(event.target instanceof Node) || !host.contains(event.target)) return;
      const p = toNdc(event);
      engine.press(p.x, p.y, event.pointerType === 'touch');
    };
    const ARROWS: Record<string, [number, number]> = {
      ArrowLeft: [-NUDGE_DEG, 0],
      ArrowRight: [NUDGE_DEG, 0],
      ArrowUp: [0, NUDGE_DEG],
      ArrowDown: [0, -NUDGE_DEG],
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') engine.surface();
      if (!engine.inScope()) return;
      const arrow = ARROWS[event.key];
      if (arrow) {
        event.preventDefault();
        engine.nudge(arrow[0], arrow[1]);
      }
      if (event.key === '+' || event.key === '=') engine.zoom(1 / ZOOM_STEP);
      if (event.key === '-' || event.key === '_') engine.zoom(ZOOM_STEP);
    };
    // the wheel (and a trackpad's pinch, which arrives as a ctrl-wheel) zooms, smoothly
    const onWheel = (event: WheelEvent) => {
      if (!engine.inScope()) return;
      event.preventDefault();
      engine.zoom(Math.exp(event.deltaY * (event.ctrlKey ? 0.01 : 0.0025)));
    };
    host.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('pointermove', onMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', onLeave);
    host.addEventListener('pointerdown', onDown);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);

    return () => {
      resizeObserver.disconnect();
      visibility.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointermove', onMove);
      document.documentElement.removeEventListener('pointerleave', onLeave);
      host.removeEventListener('pointerdown', onDown);
      host.removeEventListener('wheel', onWheel);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
      engine.dispose();
      engineRef.current = null;
    };
  }, [chrome, title]);

  const inScope = mode === 'diving' || mode === 'eyepiece';

  return (
    <div ref={rootRef} className="absolute inset-0 overflow-hidden bg-background">
      <div
        ref={canvasHostRef}
        className={`absolute inset-0 ${mode === 'eyepiece' ? 'cursor-grab active:cursor-grabbing' : ''}`}
        style={{ touchAction: inScope ? 'none' : 'pan-y' }}
      />

      {/* the lasers' trace of the title, glowing over the sky */}
      <canvas ref={traceRef} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true" />

      {failed && (
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_120%,hsl(var(--primary)/0.12)_0%,transparent_60%)]" />
      )}

      {/* the hovered constellation's name — text, place and opacity written by the engine */}
      {chrome && (
        <p
          ref={nameRef}
          aria-hidden="true"
          className="pointer-events-none absolute top-0 left-0 whitespace-nowrap font-mono text-[9px] text-primary/40 uppercase tracking-[0.32em] opacity-0 transition-opacity duration-[450ms] will-change-transform"
        />
      )}

      {/* the telescope's field stop: sharp inside, the sky going soft beyond it — dimmed a
          little, and blurring deeper toward the screen's edges — and a hairline at its rim.
          The engine writes the stop's radius (--field-r) and fades the whole. */}
      {chrome && (
        <div ref={fieldRef} aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-0" style={{ '--field-r': '200vmax' } as CSSProperties}>
          {FIELD_BLUR.map(({ blur, from, feather }) => {
            const mask = `radial-gradient(circle at center, transparent calc(var(--field-r) + ${from}px), #000 calc(var(--field-r) + ${from + feather}px))`;
            return (
              <div
                key={blur}
                className="absolute inset-0"
                style={{ backdropFilter: `blur(${blur}px)`, WebkitBackdropFilter: `blur(${blur}px)`, maskImage: mask, WebkitMaskImage: mask }}
              />
            );
          })}
          <div
            className="absolute inset-0"
            style={{
              // an even veil past a short feather: a long dark gradient bands into rings
              background:
                'radial-gradient(circle at center, transparent var(--field-r), hsl(var(--background) / 0.25) calc(var(--field-r) + 30px))',
            }}
          />
          <span
            className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 rounded-full border border-primary/20"
            style={{ width: 'calc(var(--field-r) * 2)', height: 'calc(var(--field-r) * 2)' }}
          />
        </div>
      )}

      {/* planets that are up: on the planet when it is in the field, else on the rim toward
          it — placed by the engine; a click slews there */}
      {chrome && (
        <div className="pointer-events-none absolute inset-0">
          {PLANETS_UP.map((planet) => (
            <div
              key={planet.id}
              ref={(el) => {
                if (el) planetRefs.current.set(planet.id, el);
                else planetRefs.current.delete(planet.id);
              }}
              data-at="rim"
              data-side="right"
              className="group absolute top-0 left-0 opacity-0 transition-opacity duration-700 will-change-transform"
            >
              <button
                type="button"
                onClick={() => engineRef.current?.goToPlanet(planet.id)}
                aria-label={`apuntar a ${planet.name}`}
                className="-translate-x-1/2 -translate-y-1/2 absolute flex size-7 items-center justify-center"
              >
                <span className="block size-1 rounded-full bg-primary/45 transition-all duration-500 group-hover:bg-primary group-data-[at=disc]:opacity-0 group-data-[at=in]:size-5 group-data-[at=in]:border group-data-[at=in]:border-primary/35 group-data-[at=in]:bg-transparent" />
              </button>
              <span className="-translate-y-1/2 pointer-events-none absolute left-3 whitespace-nowrap font-mono text-[8px] text-primary/35 uppercase tracking-[0.3em] transition-colors duration-300 group-hover:text-primary/80 group-data-[at=disc]:left-[calc(var(--r)+12px)] group-data-[at=in]:left-5 group-data-[side=left]:right-3 group-data-[side=left]:left-auto group-data-[at=disc]:group-data-[side=left]:right-[calc(var(--r)+12px)] group-data-[at=in]:group-data-[side=left]:right-5">
                {planet.name}
              </span>
            </div>
          ))}
        </div>
      )}

      {chrome && mode === 'intro' && (
        <button
          type="button"
          onClick={() => engineRef.current?.skip()}
          className="absolute top-4 right-4 z-[5] font-mono text-[10px] text-primary/60 uppercase tracking-[0.2em] transition hover:text-primary md:top-auto md:right-8 md:bottom-12"
        >
          saltar ››
        </button>
      )}

      {/* what the model is: a caption, once it is whole and turning; out of the way while
          looking through it */}
      {chrome && (
        <p
          className={`pointer-events-none absolute bottom-28 left-4 z-[5] max-w-[12rem] font-mono text-[9px] text-primary/45 uppercase leading-relaxed tracking-[0.2em] transition-opacity duration-700 sm:max-w-none md:bottom-12 md:left-8 ${built && !inScope ? 'opacity-100' : 'opacity-0'}`}
        >
          Extremely Large Telescope (ELT), Atacama, Chile. 2028.
        </p>
      )}

      {/* through the telescope: what is in the middle of the field, and the way back */}
      {inScope && (
        <div className="pointer-events-none absolute inset-0 z-20 select-none font-mono text-primary">
          {focus && (
            <div
              key={`${focus.name}|${focus.object}`}
              className="pointer-events-none absolute top-6 right-5 left-5 max-w-[20rem] [animation:landingFadeIn_600ms_cubic-bezier(0.22,1,0.36,1)_both] md:top-10 md:left-10"
            >
              {focus.name && <p className="text-[10px] text-primary/50 uppercase tracking-[0.24em]">{focus.name}</p>}
              {focus.object && <h2 className="mt-2 font-title text-2xl uppercase leading-none md:text-4xl">{focus.object}</h2>}
              {focus.detail && <p className="mt-3 text-[10px] text-primary/70 uppercase tracking-[0.16em]">{focus.detail}</p>}
              {focus.fact && <p className="mt-4 text-[11px] text-primary/55 leading-relaxed">{focus.fact}</p>}
            </div>
          )}
          <div className="pointer-events-auto absolute inset-x-0 bottom-5 mx-auto flex w-fit items-center justify-center gap-1 [animation:landingFadeIn_600ms_cubic-bezier(0.22,1,0.36,1)_0.8s_both] md:bottom-9">
            <ScopeButton label="anterior" onClick={() => engineRef.current?.step(-1)} icon={ChevronLeft} />
            <ScopeButton label="volver" keyHint="esc" onClick={() => engineRef.current?.surface()} icon={X} />
            <ScopeButton label="siguiente" onClick={() => engineRef.current?.step(1)} icon={ChevronRight} />
            <span className="mx-2 h-4 w-px bg-primary/15" aria-hidden="true" />
            <ScopeButton label="alejar" keyHint="−" onClick={() => engineRef.current?.zoom(ZOOM_STEP)} icon={ZoomOut} />
            <ScopeButton label="acercar" keyHint="+" onClick={() => engineRef.current?.zoom(1 / ZOOM_STEP)} icon={ZoomIn} />
          </div>
        </div>
      )}
    </div>
  );
}
